// Ícones SEMPRE na variante outline — mesma decisão do menu lateral
import {
  HiOutlineUsers,
  HiOutlineUser,
  HiOutlineUserGroup,
  HiOutlineAdjustments,
  HiOutlineCash,
  HiOutlineCog,
  HiOutlineUserRemove,
  HiOutlineBadgeCheck,
  HiOutlineTag,
  HiOutlinePuzzle,
  HiOutlineTrendingUp,
  HiOutlineCollection,
  HiOutlineRefresh,
  HiOutlineIdentification,
  HiOutlineViewGrid,
  HiOutlineDocumentText,
  HiOutlineViewList,
  HiOutlineMail,
  HiOutlineServer,
  HiOutlineShieldCheck,
  HiOutlineTemplate,
  HiOutlineCode,
  HiOutlineCurrencyDollar,
  HiOutlineLibrary,
  HiOutlineFolder,
} from "react-icons/hi";
import { FaRegIdCard } from "react-icons/fa";
import BallotIcon from "@mui/icons-material/BallotOutlined";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";
import { IuguIcon } from "../components/IuguIcon";

// Ícones do MUI ignoram a prop `size` dos react-icons (usam fontSize) —
// este adaptador faz os dois mundos conviverem no breadcrumb sem o
// ícone MUI sair maior que os vizinhos
const muiIcon = (Icon: React.ElementType): React.ElementType => {
  const Adapted = ({ size }: { size?: number }) => (
    <Icon sx={{ fontSize: size ?? 24 }} />
  );
  return Adapted;
};

// ------------------------------------------------------------------
// Ícone de cada área do sistema, por rota.
//
// Espelha os ícones do menu lateral (components/AppMenus.tsx) para que o
// breadcrumb mostre o MESMO ícone da seção em que a pessoa está — se um
// ícone mudar no menu, mudar aqui também.
// ------------------------------------------------------------------

export const resourceIcons: Record<string, React.ElementType> = {
  "/dashboard": HiOutlineTemplate,
  "/users/crud": HiOutlineUsers,
  "/orphaned-users/crud": HiOutlineUserRemove,
  "/user-elections/crud": muiIcon(BallotIcon),
  "/user-position-roles/crud": FaRegIdCard,
  "/user-role-status/crud": HiOutlineBadgeCheck,
  "/user-categories/crud": HiOutlineTag,
  "/technical-divisions/crud": HiOutlinePuzzle,
  "/graduations/crud": muiIcon(SchoolIcon),
  "/educational-institutions/crud": HiOutlineLibrary,
  "/transactions/crud": HiOutlineTrendingUp,
  "/transaction-categories/crud": HiOutlineCollection,
  "/subscriptions": HiOutlineRefresh,
  "/iugu-customers": HiOutlineIdentification,
  "/iugu-planos": HiOutlineViewGrid,
  "/iugu-invoices": HiOutlineDocumentText,
  "/my-invoices": HiOutlineCurrencyDollar,
  "/my-subscriptions": HiOutlineRefresh,
  "/products/crud": HiOutlineViewList,
  "/mail-templates/crud": HiOutlineMail,
  "/system-settings/crud": HiOutlineServer,
  "/user-roles/crud": HiOutlineShieldCheck,
  "/feedbacks/crud": muiIcon(TextsmsOutlinedIcon),
  "/api": HiOutlineCode,
  // Cadastro público de sócio — a tela usa firstPath="registrar"
  "/register": HiOutlineUser,
  "/registrar": HiOutlineUser,
};

// Normaliza os formatos que chegam ao breadcrumb
// ("/#/users/crud", "users/crud", "/users/crud", com query string…)
const normalizePath = (path: string): string =>
  "/" + path.replace(/^\/?#?\/?/, "").split("?")[0].replace(/\/$/, "");

/**
 * Ícone da área a partir do caminho usado no breadcrumb.
 */
export const iconForPath = (path?: string): React.ElementType => {
  if (!path) return HiOutlineFolder;
  return resourceIcons[normalizePath(path)] ?? HiOutlineFolder;
};

// ------------------------------------------------------------------
// Grupo (seção do menu lateral) de cada rota — permite ao breadcrumb
// mostrar o caminho completo, ex.: Gestão de Membros › Eleições.
// Espelha a estrutura de AppMenus.tsx — se um item mudar de grupo no
// menu, mudar aqui também. Itens avulsos (Dashboard, Contatos) ficam
// de fora de propósito: não têm seção acima deles.
// ------------------------------------------------------------------

type ResourceGroup = { label: string; Icon: React.ElementType };

const membros: ResourceGroup = {
  label: "Gestão de Membros",
  Icon: HiOutlineUserGroup,
};
const relacionamentos: ResourceGroup = {
  label: "Gestão de Relacionamentos",
  Icon: HiOutlineAdjustments,
};
const transacoes: ResourceGroup = { label: "Transações", Icon: HiOutlineCash };
const iugu: ResourceGroup = {
  label: "Iugu (Integração)",
  Icon: IuguIcon,
};
const sistema: ResourceGroup = { label: "Sistema", Icon: HiOutlineCog };

export const resourceGroups: Record<string, ResourceGroup> = {
  "/users/crud": membros,
  "/orphaned-users/crud": membros,
  "/user-elections/crud": membros,
  "/user-position-roles/crud": relacionamentos,
  "/user-role-status/crud": relacionamentos,
  "/user-categories/crud": relacionamentos,
  "/technical-divisions/crud": relacionamentos,
  "/graduations/crud": relacionamentos,
  "/educational-institutions/crud": relacionamentos,
  "/transactions/crud": transacoes,
  "/transaction-categories/crud": transacoes,
  "/subscriptions": iugu,
  "/iugu-customers": iugu,
  "/iugu-planos": iugu,
  "/iugu-invoices": iugu,
  "/products/crud": iugu,
  "/mail-templates/crud": sistema,
  "/system-settings/crud": sistema,
  "/user-roles/crud": sistema,
};

/**
 * Seção do menu a que a rota pertence (undefined para itens avulsos).
 */
export const groupForPath = (path?: string): ResourceGroup | undefined => {
  if (!path) return undefined;
  return resourceGroups[normalizePath(path)];
};
